import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api/client'
import { useQuery } from '../api/hooks'
import type {
  Trade,
  TradeStatus,
  TxReceipt,
  TxReceiptStatus,
} from '../api/types'
import { Badge, SidePill } from '../components/ui/Badge'
import type { BadgeTone } from '../components/ui/Badge'
import { Drawer } from '../components/ui/Drawer'
import { ExternalLink } from '../components/ui/ExternalLink'
import { Panel } from '../components/ui/Panel'
import { EmptyState, ErrorState, QueryView, Spinner } from '../components/ui/States'
import {
  CHAIN_HISTORY_START,
  explorerTokenUrl,
  explorerTxUrl,
} from '../config'
import { fmtNum, fmtTime, fmtUsd, shortAddr, shortHash } from '../lib/format'

/** D1 trade-status vocabulary (workers/agent migrations). */
const STATUS_TONES: Record<TradeStatus, BadgeTone> = {
  planned: 'dim',
  approved: 'lime',
  submitted: 'orange',
  confirmed: 'pos',
  failed: 'neg',
  abandoned: 'gray',
}

const RECEIPT_TONES: Record<TxReceiptStatus, BadgeTone> = {
  pending: 'orange',
  confirmed: 'pos',
  failed: 'neg',
  paper: 'dim',
}

const PAGE_SIZE = 50

export function Trades() {
  const [items, setItems] = useState<Trade[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [phase, setPhase] = useState<'loading' | 'error' | 'ready'>('loading')
  const [error, setError] = useState<string>('')
  const [loadingMore, setLoadingMore] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const alive = useRef(true)

  const loadFirst = useCallback(() => {
    setPhase('loading')
    api
      .trades({ limit: PAGE_SIZE })
      .then((res) => {
        if (!alive.current) return
        setItems(res.items)
        setNextCursor(res.nextCursor)
        setPhase('ready')
      })
      .catch((err: unknown) => {
        if (!alive.current) return
        setError(err instanceof Error ? err.message : String(err))
        setPhase('error')
      })
  }, [])

  useEffect(() => {
    alive.current = true
    loadFirst()
    return () => {
      alive.current = false
    }
  }, [loadFirst])

  const loadMore = () => {
    if (!nextCursor || loadingMore) return
    setLoadingMore(true)
    api
      .trades({ cursor: nextCursor, limit: PAGE_SIZE })
      .then((res) => {
        if (!alive.current) return
        setItems((prev) => [...prev, ...res.items])
        setNextCursor(res.nextCursor)
      })
      .catch((err: unknown) => {
        if (!alive.current) return
        setError(err instanceof Error ? err.message : String(err))
        setPhase('error')
      })
      .finally(() => {
        if (alive.current) setLoadingMore(false)
      })
  }

  return (
    <div className="space-y-6">
      <Panel title="trade history">
        {phase === 'loading' ? (
          <Spinner />
        ) : phase === 'error' ? (
          <ErrorState message={error} onRetry={loadFirst} />
        ) : items.length === 0 ? (
          <EmptyState
            title="no trades yet"
            detail={`The agent has not executed any trades. Chain history begins ${CHAIN_HISTORY_START}; the log fills after the first executed rebalance.`}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead>
                <tr>
                  <th className="th">time</th>
                  <th className="th">side</th>
                  <th className="th">token</th>
                  <th className="th text-right">qty</th>
                  <th className="th text-right">est price</th>
                  <th className="th">router</th>
                  <th className="th">status</th>
                  <th className="th">reason</th>
                </tr>
              </thead>
              <tbody>
                {items.map((trade) => (
                  <tr
                    key={trade.id}
                    className="cursor-pointer hover:bg-crt-panel2"
                    onClick={() => setSelectedId(trade.id)}
                  >
                    <td className="td text-xs text-crt-dim">
                      {fmtTime(trade.createdAt)}
                    </td>
                    <td className="td">
                      <SidePill side={trade.side === 'buy' ? 'BUY' : 'SELL'} />
                    </td>
                    <td className="td text-crt-lime">
                      {trade.symbol ?? shortAddr(trade.tokenAddress)}
                    </td>
                    <td className="td text-right">{trade.qty}</td>
                    <td className="td text-right">
                      {fmtUsd(trade.estPriceUsd)}
                    </td>
                    <td className="td text-xs text-crt-dim">
                      {trade.router ?? '—'}
                    </td>
                    <td className="td">
                      <Badge tone={STATUS_TONES[trade.status] ?? 'gray'}>
                        {trade.status}
                      </Badge>
                    </td>
                    <td className="td max-w-[220px]">
                      <span
                        className="line-clamp-1 text-xs text-crt-dim"
                        title={trade.reason ?? undefined}
                      >
                        {trade.reason ?? '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-2 flex items-center justify-between text-[11px] text-crt-dim">
              <span>click a row for execution details + receipts</span>
              {nextCursor ? (
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={loadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'loading…' : 'load more'}
                </button>
              ) : null}
            </div>
          </div>
        )}
      </Panel>

      <Drawer
        open={selectedId !== null}
        title={selectedId ? `trade ${selectedId}` : 'trade'}
        onClose={() => setSelectedId(null)}
      >
        {selectedId ? <TradeDetailView id={selectedId} /> : null}
      </Drawer>
    </div>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-crt-dim/30 py-1.5 text-sm">
      <dt className="shrink-0 text-crt-dim">{label}</dt>
      <dd className="min-w-0 break-all text-right">{children}</dd>
    </div>
  )
}

function TradeDetailView({ id }: { id: string }) {
  const detail = useQuery(() => api.trade(id), [id])
  return (
    <QueryView state={detail.state} onRetry={detail.refresh}>
      {({ trade }) => (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <SidePill side={trade.side === 'buy' ? 'BUY' : 'SELL'} />
            <span className="text-lg text-crt-lime">
              {trade.symbol ?? shortAddr(trade.tokenAddress)}
            </span>
            <Badge tone={STATUS_TONES[trade.status] ?? 'gray'}>
              {trade.status}
            </Badge>
          </div>

          <dl>
            <Row label="created">{fmtTime(trade.createdAt)}</Row>
            <Row label="updated">{fmtTime(trade.updatedAt)}</Row>
            <Row label="token">
              <ExternalLink href={explorerTokenUrl(trade.tokenAddress)}>
                {shortAddr(trade.tokenAddress)}
              </ExternalLink>
            </Row>
            <Row label="qty">{trade.qty}</Row>
            <Row label="est price">{fmtUsd(trade.estPriceUsd)}</Row>
            <Row label="max slippage">{trade.maxSlippageBps} bps</Row>
            <Row label="router">{trade.router ?? '—'}</Row>
            <Row label="workflow">{trade.workflowId ?? '—'}</Row>
            <Row label="portfolio state">
              {trade.portfolioStateId ?? '—'}
            </Row>
          </dl>

          {trade.reason ? (
            <div>
              <div className="terminal-label">reason</div>
              <pre className="mt-1 overflow-x-auto border border-crt-dim/60 bg-crt-bg p-3 text-xs text-crt-gray">
                {trade.reason}
              </pre>
            </div>
          ) : null}

          <div>
            <div className="terminal-label">receipts</div>
            {trade.receipts.length === 0 ? (
              <div className="mt-1 text-xs text-crt-dim">
                no execution receipts recorded
              </div>
            ) : (
              <div className="mt-1 space-y-2">
                {trade.receipts.map((r) => (
                  <ReceiptCard key={r.id} receipt={r} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </QueryView>
  )
}

function ReceiptCard({ receipt }: { receipt: TxReceipt }) {
  return (
    <div className="border border-crt-dim/60 bg-crt-bg p-3 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={RECEIPT_TONES[receipt.status] ?? 'gray'}>
          {receipt.status}
        </Badge>
        {receipt.txHash ? (
          <ExternalLink href={explorerTxUrl(receipt.txHash)}>
            {shortHash(receipt.txHash)}
          </ExternalLink>
        ) : (
          <span className="text-crt-dim">no tx hash (paper fill)</span>
        )}
        <span className="ml-auto text-crt-dim">
          {fmtTime(receipt.updatedAt)}
        </span>
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
        <div className="flex justify-between">
          <dt className="text-crt-dim">fill price</dt>
          <dd>{fmtUsd(receipt.fillPriceUsd)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-crt-dim">fill qty</dt>
          <dd>{receipt.fillQty ?? '—'}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-crt-dim">block</dt>
          <dd>{receipt.blockNumber !== null ? fmtNum(receipt.blockNumber, 0) : '—'}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-crt-dim">confirmations</dt>
          <dd>{receipt.confirmations}</dd>
        </div>
      </dl>
    </div>
  )
}
